import { defineComponent as _defineComponent } from 'vue'
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { Message } from 'element-ui'
import CareerAI from '@/components/career-ai/CareerAI.vue'
import CareerAiConsentDialog from '@/components/career-ai/CareerAiConsentDialog.vue'
import LoginDialog from '@/components/LoginDialog.vue'
import careerAIClient from '@/services/career-ai-client'
import { useAuth } from '@/composables/useAuth'
import { createTrackEvent } from '@/composables/ai/useCareerAiTracking'
import { emitter, emitOpenLoginDialog, LOGIN_SUCCESS } from '@/plugins/emitter'


export default /*#__PURE__*/_defineComponent({
  __name: 'CareerAiLauncher',
  props: {
    placement: null
  },
  setup(__props: any) {

const props = __props as {
  /** 設置場所。計測の placement と、ボタン id の接尾辞に使う */
  placement: string
};

/**
 * AIチャット（ヤギコンシェルジュ）のフローティングボタンと、開いたときのチャット窓。
 * マイページ以外のページにボットを置くための入口。未ログインならログインダイアログを
 * 出し、ログインできたらそのまま開く。
 */


const DEFAULT_LIMIT = 30

const { user } = useAuth()

const visible = ref(false)
const mounted = ref(false)
const uuid = ref('')
const sessionId = ref('')
const initialMode = ref('concierge')
const remaining = ref(DEFAULT_LIMIT)
const dailyLimit = ref(DEFAULT_LIMIT)
const consentVisible = ref(false)
const consentSubmitting = ref(false)
const loginDialogMounted = ref(false)
/** ログイン要求で中断した起動。ログイン成功後に続きを行う */
let pendingOpen = false

const trackEvent = createTrackEvent(
  () => uuid.value,
  () => sessionId.value
)

function notifyError(message?: string) {
  Message({
    type: 'error',
    message:
      message ||
      'ヤギコンシェルジュの起動に失敗しました。少し時間をおいてお試しください。'
  })
}

function applyStart(res: {
  session_id: string
  current_mode?: string
  remaining?: number
  daily_limit?: number
}) {
  sessionId.value = res.session_id
  initialMode.value = res.current_mode || 'concierge'
  if (typeof res.remaining === 'number') remaining.value = res.remaining
  if (typeof res.daily_limit === 'number') dailyLimit.value = res.daily_limit
}

async function startAndShow() {
  // chatStart は残メッセージ数を消費するので、同じページ内では再発行しない
  if (mounted.value && sessionId.value) {
    visible.value = true
    return
  }
  try {
    const res = await careerAIClient.chatStart(uuid.value)
    if (res && res.success) {
      applyStart(res)
      mounted.value = true
      visible.value = true
    } else {
      notifyError(res && res.message)
    }
  } catch (e: any) {
    if (e && e.status === 403 && e.code === 'CONSENT_REQUIRED') {
      consentVisible.value = true
    } else {
      notifyError(e && e.message)
    }
  }
}

async function openForUser(uid: string) {
  uuid.value = uid
  try {
    const consent = await careerAIClient.getConsent(uid)
    if (!consent || !consent.consented) {
      consentVisible.value = true
      return
    }
  } catch (_) {
    consentVisible.value = true
    return
  }
  await startAndShow()
}

async function openLogin() {
  pendingOpen = true
  // mount 直後でないと OPEN_LOGIN_DIALOG の購読が始まっていない
  loginDialogMounted.value = true
  await nextTick()
  emitOpenLoginDialog({ trigger: `career_ai_fab_${props.placement}` })
}

function open() {
  const uid = user.value?.uid
  trackEvent('career_ai_fab_click', {
    placement: props.placement,
    is_logged_in: !!uid
  })
  if (!uid) {
    openLogin()
    return
  }
  openForUser(uid)
}

function onLoginSuccess() {
  if (!pendingOpen) return
  pendingOpen = false
  const uid = user.value?.uid
  if (uid) openForUser(uid)
}

async function restartSession() {
  // 旧セッションを終了して新しく発行する。session_id が変わると CareerAI が履歴を初期化する
  try {
    await careerAIClient.chatEnd(uuid.value)
  } catch (_) {
    // 終了に失敗しても新規発行は試みる
  }
  try {
    const res = await careerAIClient.chatStart(uuid.value)
    if (res && res.success) {
      applyStart(res)
      trackEvent('career_ai_clear_history', { placement: props.placement })
    } else {
      notifyError(res && res.message)
    }
  } catch (e: any) {
    notifyError(e && e.message)
  }
}

async function acceptConsent() {
  consentSubmitting.value = true
  trackEvent('career_ai_consent_accept', { placement: props.placement })
  try {
    await careerAIClient.setConsent(uuid.value, true)
    consentVisible.value = false
    await startAndShow()
  } catch (e: any) {
    notifyError(e && e.message)
  } finally {
    consentSubmitting.value = false
  }
}

function declineConsent(reason: string) {
  trackEvent('career_ai_consent_decline', {
    placement: props.placement,
    reason
  })
  consentVisible.value = false
}

onMounted(() => emitter.on(LOGIN_SUCCESS, onLoginSuccess))
onBeforeUnmount(() => emitter.off(LOGIN_SUCCESS, onLoginSuccess))

return { __sfc: true,props, DEFAULT_LIMIT, user, visible, mounted, uuid, sessionId, initialMode, remaining, dailyLimit, consentVisible, consentSubmitting, loginDialogMounted, pendingOpen, trackEvent, notifyError, applyStart, startAndShow, openForUser, openLogin, open, onLoginSuccess, restartSession, acceptConsent, declineConsent, CareerAI, CareerAiConsentDialog, LoginDialog }
}

})